import React, { useCallback, useState } from 'react';

import SuccessModalContent from '@editor/components/SuccessModalContent/SuccessModalContent';
import DecisionModal from '@repo/editor/src/components/DecisionModal/DecisionModal';
import { SubscriptionModalContent } from '@repo/editor/src/components/Subscription';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '@repo/editor/src/stores/planSelectionStore';
import {
  teamsStoreSelector,
  useTeamsStore,
} from '@repo/editor/src/stores/teamsStore';
import { ChooseTeamPlanFlowState } from '@repo/editor/src/stores/teamsStore/types';
import { withEventScope } from '@repo/event-processor';
import { SubscriptionPlanType } from '@repo/subscription/api';

import { StyledTeamPlans } from './styles';
import { ChooseTeamPlanFlowContentProps } from './types';

export const INITIAL_DIMENSIONS = { width: '1098px' };

const ChooseTeamPlanFlowContent: React.FC<ChooseTeamPlanFlowContentProps> = ({
  onClose,
  showFreePlan,
  canChooseFreePlan,
  onSuccess,
  onSuccessLabel,
  onSuccessMessage,
  onDimensionsChange,
  initialQuantity,
}) => {
  const state = useTeamsStore(teamsStoreSelector.chooseTeamPlanFlowState);
  const setState = useTeamsStore(teamsStoreSelector.setChooseTeamPlanFlowState);

  const [showFreePlanModal, setShowFreePlanModal] = useState(false);

  const handleSetState = useCallback(
    (state: ChooseTeamPlanFlowState) => {
      setState(state);
      const modalDimensions =
        state === ChooseTeamPlanFlowState.ChooseTeamPlan
          ? INITIAL_DIMENSIONS
          : {
              width: '940px',
              height: '720px',
            };
      onDimensionsChange?.(modalDimensions);
    },
    [onDimensionsChange, setState]
  );

  const { setPlan, setYearly } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );

  const handlePaymentSuccess = (): void => {
    handleSetState(ChooseTeamPlanFlowState.Success);
  };

  const handleSuccess = (): void => {
    if (onSuccess) {
      onSuccess();
      return;
    }
    onClose();
  };

  const handleSelectPlan = (
    plan: SubscriptionPlanType,
    isYearly: boolean
  ): void => {
    if (plan === SubscriptionPlanType.FREE) {
      if (!canChooseFreePlan) {
        setShowFreePlanModal(true);
        return;
      }
      handleSuccess();
      return;
    }
    setPlan(plan);
    setYearly(isYearly);
    handleSetState(ChooseTeamPlanFlowState.Payment);
  };

  const hideFreePlanModal = (): void => {
    setShowFreePlanModal(false);
  };

  return (
    <>
      {state === ChooseTeamPlanFlowState.ChooseTeamPlan && (
        <>
          <StyledTeamPlans
            initialQuantity={initialQuantity}
            plansToShow={[
              ...(showFreePlan ? [SubscriptionPlanType.FREE] : []),
              SubscriptionPlanType.PRO,
              SubscriptionPlanType.EXPERT,
            ]}
            onSelectPlan={handleSelectPlan}
          />
          <DecisionModal
            firstButton={{
              label: 'Got it',
              onClick: hideFreePlanModal,
              width: 'auto',
              type: 'primary',
            }}
            isOpen={showFreePlanModal}
            message="There is a limit of 5 Free teams per user. Please choose a paid team subscription, or delete one of your Free teams to create a new one."
            title="You reached the maximum number of Free teams"
            onClose={hideFreePlanModal}
          />
        </>
      )}
      {state === ChooseTeamPlanFlowState.Payment && (
        <SubscriptionModalContent
          hideSuccessModal
          onSuccess={handlePaymentSuccess}
        />
      )}
      {state === ChooseTeamPlanFlowState.Success && (
        <SuccessModalContent
          onSuccess={handleSuccess}
          onSuccessLabel={onSuccessLabel}
          onSuccessMessage={onSuccessMessage}
        />
      )}
    </>
  );
};

export default withEventScope(ChooseTeamPlanFlowContent, {
  feature: 'chooseTeamPlanFlow',
});
